import { useState, type FormEvent } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router';
import { useAuth } from '../../auth/AuthContext';
import { isApiError } from '../../api/client';
import { safeNext } from '../../lib/next';
import { Button } from '../../ui/Button';
import { Field, FormError } from '../../ui/Field';
import { AuthLayout } from './AuthLayout';

type Errors = { name?: string; email?: string; password?: string; form?: string };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function validateSignup(v: { name: string; email: string; password: string }): Errors {
  const e: Errors = {};
  if (v.name.trim().length > 60) e.name = 'Keep your name under 60 characters.';
  if (!v.email.trim()) e.email = 'Enter your email.';
  else if (!EMAIL_RE.test(v.email.trim()) || v.email.trim().length > 254) e.email = 'Enter a valid email address.';
  if (v.password.length < 8) e.password = 'Use at least 8 characters.';
  else if (v.password.length > 128) e.password = 'Use at most 128 characters.';
  return e;
}

export function Signup() {
  const { signup, isAuthenticated } = useAuth();
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  const [busy, setBusy] = useState(false);

  if (isAuthenticated) return <Navigate to={next} replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const errs = validateSignup({ name, email, password });
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      await signup({ email: email.trim(), password, ...(name.trim() ? { name: name.trim() } : {}) });
    } catch (err) {
      if (isApiError(err) && err.status === 409) {
        setErrors({ email: 'An account with this email already exists. Try signing in.' });
      } else if (isApiError(err) && err.status === 400) {
        setErrors({ form: err.messages.join('\n') });
      } else {
        setErrors({ form: isApiError(err) ? err.message : 'Could not create your account. Try again.' });
      }
      setBusy(false);
    }
  }

  const loginHref = params.get('next') ? `/login?next=${encodeURIComponent(next)}` : '/login';

  return (
    <AuthLayout
      title="Create your account"
      subtitle="Shared shopping lists for the whole family."
      footer={
        <>
          Already have an account?{' '}
          <Link to={loginHref} className="font-semibold text-accent underline-offset-4 hover:underline">
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate className="space-y-4">
        <FormError>{errors.form && <span className="whitespace-pre-line">{errors.form}</span>}</FormError>
        <Field
          label="Name (optional)"
          name="name"
          autoComplete="name"
          autoCapitalize="words"
          enterKeyHint="next"
          maxLength={60}
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={errors.name}
        />
        <Field
          label="Email"
          name="email"
          type="email"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="next"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={errors.email}
          required
        />
        <Field
          label="Password"
          name="password"
          type="password"
          autoComplete="new-password"
          autoCapitalize="none"
          enterKeyHint="go"
          minLength={8}
          maxLength={128}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={errors.password}
          hint="At least 8 characters."
          required
        />
        <Button type="submit" size="lg" block loading={busy}>
          {busy ? 'Creating account…' : 'Create account'}
        </Button>
      </form>
    </AuthLayout>
  );
}
